import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTelematicsFeature } from '@acorn-portal/shared/src/hooks/useTelematicsFeature';
import { useDriverScoreVisibility } from '@acorn-portal/shared/src/hooks/useDriverScoreVisibility';
import { fetchAverageScores, fetchInsights, clearAll } from '@acorn-portal/shared/src/store/telematicsDriverScoreSlice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import DriverScoreGauge from './driver-score-gauge';
import DriverScoreRow from './driver-score-row';
import DriverScoreTabs from './driver-score-tabs';
import DriverScoreEmptyState from './driver-score-empty-state';
import { TimePeriod, extractScoresFromResponse, getPeriodDays, shouldShowEmptyState } from './driver-score-utils';

/**
 * Props for the DriverScoreCard component.
 */
interface DriverScoreCardProps {
	/** Policy number used to fetch driver score data from the telematics API. */
	policyNumber: string;
}

/**
 * Main driver score dashboard card displaying overall and period-based scores.
 *
 * Orchestrates the display of driver scores including a circular gauge for the
 * total score, individual metric rows (smoothness, speed, usage), and time
 * period filtering tabs. Fetches data from the telematics API on mount and
 * when the time period changes.
 *
 * Renders nothing when the driver-score visibility flag is off.
 *
 * @component
 * @example
 * ```tsx
 * <DriverScoreCard policyNumber="POL-12345" />
 * ```
 */
const DriverScoreCard: React.FC<DriverScoreCardProps> = ({ policyNumber }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();

	// Telematics feature flag - controls whether API calls are made
	const isTelematicsFeatureEnabled = useTelematicsFeature();

	// Driver score visibility flag - nothing score-related renders or fetches when off
	const showDriverScore = useDriverScoreVisibility();

	const [timePeriod, setTimePeriod] = useState<TimePeriod>('overall');

	const { averageScores, averageScoresLoading, insights, insightsLoading } = useAppSelector(
		(state) => state.telematicsDriverScore,
	);

	// Fetch data on mount (only if feature flag is enabled)
	useEffect(() => {
		if (isTelematicsFeatureEnabled && showDriverScore && policyNumber) {
			dispatch(fetchAverageScores({ policyReference: policyNumber }));
		}

		return () => {
			dispatch(clearAll());
		};
	}, [dispatch, policyNumber, isTelematicsFeatureEnabled, showDriverScore]);

	// Fetch insights when tab changes (only if feature flag is enabled)
	useEffect(() => {
		if (isTelematicsFeatureEnabled && showDriverScore && policyNumber && timePeriod !== 'overall') {
			const periodDays = getPeriodDays(timePeriod);
			dispatch(fetchInsights({ policyReference: policyNumber, periodDays }));
		}
	}, [dispatch, policyNumber, timePeriod, isTelematicsFeatureEnabled, showDriverScore]);

	if (!showDriverScore) return null;

	const handleViewTrips = () => {
		navigate(ROUTE_PATHS.telematicsTrips, { state: { from: location.pathname + location.search } });
	};

	const scores = extractScoresFromResponse(timePeriod, averageScores, insights);
	const isLoading = averageScoresLoading || (timePeriod !== 'overall' && insightsLoading);
	const noScoreData = shouldShowEmptyState(averageScores, isLoading, scores);

	return (
		<Box>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					mb: 2,
				}}
			>
				<Typography
					variant='h5'
					sx={{
						fontWeight: 500,
						color: theme.palette.secondary.main,
					}}
				>
					Your driver score
				</Typography>
			</Box>

			{/* Main Content - Gauge and Scores or No Data Message */}
			{noScoreData ? (
				<DriverScoreEmptyState timePeriod={timePeriod} />
			) : (
				<Box
					sx={{
						display: 'flex',
						gap: 3,
						mb: 3,
						opacity: isLoading ? 0.5 : 1,
						transition: 'opacity 0.2s ease',
					}}
				>
					{/* Circular Gauge */}
					<Box sx={{ flexShrink: 0 }}>
						<DriverScoreGauge score={scores.total} />
					</Box>

					{/* Score Breakdown */}
					<Box sx={{ flex: 1 }}>
						<DriverScoreRow label='Smoothness' score={scores.smoothness} />
						<DriverScoreRow label='Speed' score={scores.speed} />
						<DriverScoreRow label='Usage' score={scores.usage} />
					</Box>
				</Box>
			)}

			{/* Time Period Tabs */}
			<DriverScoreTabs value={timePeriod} onChange={setTimePeriod} />

			{/* View Recent Trips Button */}
			<Button
				variant='outlined'
				fullWidth
				color='primary'
				onClick={handleViewTrips}
				sx={{
					whiteSpace: 'nowrap',
					marginX: 'auto',
					p: 1,
					fontSize: 17,
					textTransform: 'capitalize',
					fontWeight: 600,
					border: `2px solid ${theme.palette.primary.main}`,
					'&:hover': {
						backgroundColor: 'transparent',
						border: `2px solid ${theme.palette.primary.main}`,
					},
					'&.MuiButton-outlined': {
						border: `2px solid ${theme.palette.primary.main}`,
					},
				}}
			>
				View recent trips
			</Button>
		</Box>
	);
};

export default DriverScoreCard;
